import React from 'react'
import { Tag, Typography } from 'antd'
import { ColDef } from 'ag-grid-enterprise'
import { CustomCellRendererProps } from 'ag-grid-react'
import { TFunction, useTranslation } from 'react-i18next'
import { Dimension } from '../../../../../../features/dimension/types/Dimension'
import { ExpandAllHeaderRenderer } from '../../../../../../shared/components/custom/Grid/cellRenderers'
import { isOwnSource, tagLabel } from '../../utils/dimensionAccess'
import DimensionRowMenuCellRenderer from '../DimensionRowMenuCellRenderer'

const { Text } = Typography

export interface DimensionGridContext {
  flatList: Dimension[]
  canEdit: boolean
  onRequestRename: (dimension: Dimension) => void
  onRequestMoveUnder: (dimensionIds: string[]) => void
  onRequestAddChild: (parentDimensionId: string) => void
  onRequestDelete: (dimensionIds: string[]) => void
  onDuplicate: (dimension: Dimension) => void
}

const DimensionNameInnerRenderer: React.FC<CustomCellRendererProps<Dimension>> = ({ data, value }) => (
  <Text strong={!!data?.children?.length}>{value}</Text>
)

const DimensionSourceCellRenderer: React.FC<CustomCellRendererProps<Dimension>> = ({ data }) =>
  data ? <Tag color={isOwnSource(data.source) ? 'blue' : undefined}>{tagLabel(data.source)}</Tag> : null

const DimensionDefaultCellRenderer: React.FC<CustomCellRendererProps<Dimension>> = ({ data }) => {
  const { t } = useTranslation()
  if (!data?.default) return null
  return <Tag color="gold">{t('dimensionsPage:tagDefault')}</Tag>
}

const actionsColumn: ColDef<Dimension> = {
  colId: 'rowMenu',
  headerName: '',
  pinned: 'right',
  sortable: false,
  filter: false,
  width: 36,
  resizable: false,
  suppressSizeToFit: true,
  cellStyle: { padding: 0 },
  cellRenderer: DimensionRowMenuCellRenderer
}

export const buildAutoGroupColumnDef = (t: TFunction, dragEnabled: boolean): ColDef => ({
  headerName: t('global:name'),
  field: 'name',
  pinned: 'left',
  flex: 4,
  minWidth: 360,
  rowDrag: dragEnabled,
  cellRendererParams: { innerRenderer: DimensionNameInnerRenderer },
  headerComponent: ExpandAllHeaderRenderer,
  suppressMovable: true
})

export const buildTreeColumnDefs = (t: TFunction, canEdit: boolean): ColDef<Dimension>[] => [
  { field: 'dimensionId', headerName: t('global:code'), width: 180 },
  {
    field: 'source',
    headerName: t('dimensionsPage:tagSourceLabel'),
    width: 140,
    cellRenderer: DimensionSourceCellRenderer
  },
  {
    field: 'default',
    headerName: t('dimensionsPage:inspectorDefault'),
    flex: 1,
    minWidth: 100,
    cellRenderer: DimensionDefaultCellRenderer
  },
  ...(canEdit ? [actionsColumn] : [])
]

export const buildListColumnDefs = (
  t: TFunction,
  dimensionNameById: Map<string, string>,
  canEdit: boolean
): ColDef<Dimension>[] => [
  { field: 'name', headerName: t('global:name'), flex: 4, minWidth: 360, sortable: true, filter: true },
  {
    field: 'parentDimensionId',
    headerName: t('dimensionsPage:inspectorParent'),
    flex: 1,
    minWidth: 160,
    valueFormatter: ({ value }) => (value ? dimensionNameById.get(value) || value : ''),
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: ({ value }: { value: string | null }) => (value ? dimensionNameById.get(value) || value : '—')
    }
  },
  { field: 'dimensionId', headerName: t('global:code'), width: 140, filter: 'agTextColumnFilter' },
  {
    field: 'source',
    headerName: t('dimensionsPage:tagSourceLabel'),
    width: 140,
    cellRenderer: DimensionSourceCellRenderer,
    filter: 'agSetColumnFilter'
  },
  {
    field: 'default',
    headerName: t('dimensionsPage:inspectorDefault'),
    flex: 1,
    minWidth: 100,
    cellRenderer: DimensionDefaultCellRenderer,
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: ({ value }: { value: boolean }) => (value ? t('global:yes') : t('global:no'))
    }
  },
  ...(canEdit ? [actionsColumn] : [])
]
